import { useMemo } from 'react';
import { Html } from '@react-three/drei';
import useWorldStore from '../store/worldStore';
import { ACTIVITIES } from '../simulation/activityConfig';
import { resolveAct } from '../simulation/lifeSimEngine';

function getStatusText(agent) {
  switch (agent.state) {
    case 'walking':
      return 'en route';
    case 'busy':
      return agent.currentTask
        ? agent.currentTask.slice(0, 22) + (agent.currentTask.length > 22 ? '...' : '')
        : 'working';
    case 'dragged':
      return 'relocating';
    default:
      return 'standby';
  }
}

function getDotStyle(agent, zoneColor) {
  const base = {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  };

  if (agent.state === 'dragged') return { ...base, background: 'white' };
  if (agent.state === 'walking') {
    return { ...base, background: zoneColor, animation: 'dotPulse 1s ease-in-out infinite' };
  }
  return { ...base, background: zoneColor };
}

export default function TaskIcon({ agent, zone }) {
  const hoveredAgentId = useWorldStore((s) => s.hoveredAgentId);
  const indicator = useWorldStore((s) => s.agentIndicators?.[agent.id]);
  const isHovered = hoveredAgentId === agent.id;
  const zoneColor = zone?.color || '#888';

  // Check for active indicator (chat/task pulsing)
  const isPulsing = !!indicator?.pulsing;
  let indicatorIcon = null;
  if (indicator?.type === 'chat') indicatorIcon = '💬';
  else if (indicator?.type === 'task') indicatorIcon = zone?.icon || '⚡';

  // Use activity-specific emoji for life mode, zone icon for work
  let activityEmoji = null;
  if (agent.activity) {
    activityEmoji = ACTIVITIES[agent.activity]?.emoji;
    if (!activityEmoji) {
      const resolved = resolveAct(agent.activity);
      activityEmoji = resolved?.emoji;
    }
  }
  const baseIcon = (agent.mode === 'life' && activityEmoji) ? activityEmoji : (zone?.icon || '📍');
  const zoneIcon = indicatorIcon || baseIcon;

  return (
    <Html
      position={[0, isHovered ? 3.6 : 2.6, 0]}
      center
      sprite
      occlude={false}
      zIndexRange={[0, 0]}
      style={{ pointerEvents: 'none' }}
    >
      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes indicatorPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.25); opacity: 0.7; }
        }
        .task-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          transition: all 0.2s ease-out;
        }
        .task-pill {
          background: rgba(10,12,20,0.85);
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 4px;
          pointer-events: none;
          white-space: nowrap;
          user-select: none;
          overflow: hidden;
          transition: all 0.2s ease-out;
        }
        .task-pill.collapsed {
          width: 28px;
          height: 28px;
          padding: 0;
          border: 1px solid transparent;
          border-radius: 50%;
          justify-content: center;
        }
        .task-pill.expanded {
          padding: 8px 12px;
          border-radius: 8px;
          flex-direction: column;
          align-items: flex-start;
          gap: 6px;
          min-width: 160px;
        }
      `}</style>

      <div className="task-container">
        {isHovered ? (
          <div
            className="task-pill expanded"
            style={{
              background: 'rgba(10,12,20,0.92)',
              border: `1px solid ${zoneColor}66`,
            }}
          >
            {/* Name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={getDotStyle(agent, zoneColor)} />
              <span style={{
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontSize: '12px',
                color: 'white',
                fontWeight: 500,
              }}>
                {agent.name}
              </span>
            </div>
            {/* Activity title */}
            <div style={{
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: '10px',
              color: zoneColor,
              fontWeight: 500,
            }}>
              {zoneIcon} {(() => {
                if (agent.mode === 'life' && agent.activity) {
                  const r = resolveAct(agent.activity);
                  return r?.name || agent.activity;
                }
                if (agent.state === 'walking') return 'En route';
                return 'Working';
              })()}
            </div>
            {/* Flavor text */}
            <div style={{
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: '9px',
              color: 'rgba(255,255,255,0.4)',
              maxWidth: '200px',
              lineHeight: 1.3,
            }}>
              {(() => {
                const task = agent.currentTask || '';
                // Strip emoji prefix if present
                const clean = task.replace(/^[\p{Emoji}\s]+/u, '').trim();
                return clean || agent.role;
              })()}
            </div>
          </div>
        ) : (
          <div
            className="task-pill collapsed"
            style={{
              border: `1px solid ${isPulsing ? agent.color : zoneColor}99`,
              animation: isPulsing ? 'indicatorPulse 1.2s ease-in-out infinite' : 'none',
              boxShadow: isPulsing ? `0 0 8px ${agent.color}44` : 'none',
            }}
          >
            <span style={{ fontSize: '12px', lineHeight: 1 }}>{zoneIcon}</span>
          </div>
        )}
      </div>
    </Html>
  );
}
